import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { safeReturnPath } from '@/lib/utils/safeRedirectPath';

/**
 * Story 30.1 (ruling O12) — the SPA half of a notification tap. When a push
 * is tapped and an app window is already open, `public/sw.js` focuses it and
 * posts `{ type: 'amatis:navigate', url }`; this hook routes that url through
 * react-router (no full reload), after `safeReturnPath`, so a hostile or
 * sign-in url lands on `/dashboards`. Any other message is ignored.
 *
 * Mounted once, by `PushNavigationListener` in `src/App.tsx`, inside
 * `BrowserRouter` and beside `<Routes>` — NOT in `MainLayout`: the worker
 * posts to the first same-origin window whatever its route, so a window on
 * `/login`, `/signup` or a 404 must hear the tap too. Only files under
 * `src/lib/push/` may touch the service worker API
 * (`scripts/__tests__/installable-app.test.ts`).
 */
export const NAVIGATE_MESSAGE_TYPE = 'amatis:navigate';

export function usePushNavigation(): void {
  const navigate = useNavigate();

  useEffect(() => {
    const container = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker;
    if (!container) return undefined;
    const onMessage = (event: MessageEvent) => {
      const data: unknown = event.data;
      if (!data || typeof data !== 'object') return;
      const { type, url } = data as { type?: unknown; url?: unknown };
      if (type !== NAVIGATE_MESSAGE_TYPE || typeof url !== 'string') return;
      navigate(safeReturnPath(url));
    };
    container.addEventListener('message', onMessage);
    return () => container.removeEventListener('message', onMessage);
  }, [navigate]);
}

/** Render-nothing mount point for `usePushNavigation` (see above). */
export function PushNavigationListener(): null {
  usePushNavigation();
  return null;
}
